import { faArrowRotateLeft, faChevronDown, faClose, faFilePen, faFilter, faPlus, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, CardHeader, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, Stack, Typography } from '@mui/material'
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useState } from 'react'
import AbiStatementViewApiUrls from 'src/apiUrls/AbiStatementViewApiUrls'
import { createDefaultValues } from 'src/components/era-package/forms/hooks/useFormConfig'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import { Table } from 'src/components/era-package'
import { dateRangeFilter } from 'src/utils/era-package/filters'
import { optionsMap, stmtUpdateFields, stmtUpdateCols, stmtUpdateFilters } from '../../shared'
import { enqueueSnackbar } from 'src/contexts/era-package/SnackbarContext'
import AbiEntrySummaryApiUrls from "../../../../../apiUrls/AbiEntrySummaryApiUrls";

// INTEGRATION: Backend operations
const fetchData = (setData = () => { }, setLoading = () => { }) => {
  let isAlive = true
  setLoading(true)
  fetch(AbiStatementViewApiUrls.GET_STATEMENT_UPDATES, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
    })
    .then(res => res.json())
    .then(async (result) => {
      // simulate delay
      await new Promise(resolve => setTimeout(resolve, 2000))

      if (isAlive && result.status === 200) {
        result.data.forEach(item => {
          item.entryNumber = `${item.filerCode}-${item.entryNumber}`;
        });
        setData(result.data)
      }
    }).catch(() => {
      if (isAlive) setData([])
    }).finally(() => {
      if (isAlive) setLoading(false)
    })

  return () => { isAlive = false }
}

const submitData = async (formData) => {
  // simulate delay
  await new Promise(resolve => setTimeout(resolve, 2000))

  // data parsing & submission logic here
  console.log('Submitted data:', formData)

  try {
    const response = await fetch(AbiStatementViewApiUrls.CREATE_STATEMENT_UPDATES, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: JSON.stringify(formData)  // Convert JS object to JSON string
    });

    if (!response.ok) {
      return false;
    }

    const result = await response.json();
    console.log('Form submitted successfully:', result);
    return true;
  } catch (error) {
    console.error('Error submitting form:', error);
    return false;
  }
}
// END INTEGRATION

const DEFAULT_FILTERS = createDefaultValues(stmtUpdateFilters)

export const UpdatesTab = () => {
  const filtersForm = useMemo(() => ({
    fields: stmtUpdateFilters,
    title: 'Filters',
    icon: <FontAwesomeIcon icon={faFilter} />,
    headless: true
  }), [])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(true)

  const [formData, setFormData] = useState(createDefaultValues(stmtUpdateFields))
  const [submitting, setSubmitting] = useState(false)

  const table = useReactTable({
    data, columns: stmtUpdateCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: row => row.original.statusDetails?.length > 0,
    getSubRows: row => row.statusDetails,
    getRowId: row => row.statementNumber,
    filterFns: {
      dateRange: dateRangeFilter
    },
    initialState: { pagination: { pageSize: 10 } },
    enableSorting: true,
    enableRowSelection: false,
  })

  // Initialize table data
  useEffect(() => fetchData(setData, setLoading), [])

  // Filters
  useEffect(() => {
    const tableFilterState = Object.entries(filters)
      .map(([id, value]) => ({ id, value }))
    table.setColumnFilters(tableFilterState)
  }, [table, filters])

  const handleRowExpansion = (row) => {
    const NestedRow = React.lazy(() => import('./ResponseList'));
    return (
      <React.Suspense fallback={<Skeleton />}>
        <NestedRow data={row.original} list={row.originalSubRows} />
      </React.Suspense>
    );
  }

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    let submissionOk = await submitData(formData)
    if (submissionOk)
      enqueueSnackbar('Statement update created successfully.', { severity: 'success' })
    else
      enqueueSnackbar('Internal server error.', { severity: 'error' })

    setSubmitting(false)
    setFormData(createDefaultValues(stmtUpdateFields))
    fetchData(setData, setLoading)
    setCreateDialogOpen(false)
  }
  const handleClose = () => {
    if (!submitting)
      setCreateDialogOpen(false)
  }

  return <Stack spacing={2}>
    <Stack direction='row' spacing={1}>
      <Button variant='outlined'
        startIcon={<FontAwesomeIcon icon={faChevronDown}
          rotation={filtersOpen ? 180 : 0}
        />}
        onClick={() => setFiltersOpen(!filtersOpen)}
      >
        {filtersOpen ? 'Hide' : 'Show'} Filters
      </Button>
      <Button variant='outlined'
        onClick={handleReset}
        sx={{ minWidth: 120, px: 3, py: 1 }}
        startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
      >
        Reset Filters
      </Button>
      <Button
        onClick={() => setCreateDialogOpen(true)}
        startIcon={<FontAwesomeIcon icon={faPlus} />}
      >
        Create Statement Update
      </Button>
    </Stack>

    {/* Filters */}
    <Collapse in={filtersOpen} unmountOnExit>
      <BlankCard>
        <CardHeader disableTypography
          title={<Stack direction='row' spacing={1}
            alignItems='center'
          >
            <Box component={FontAwesomeIcon}
              icon={faFilter} color='primary.main'
            />
            <Typography variant='h6' color='primary'>Filters</Typography>
          </Stack>}
          sx={{
            minHeight: '2rem',
            py: 1.5, mb: -2.5,
            backgroundColor: 'primary.50',

          }}
        />
        <RenderFormSection section={filtersForm}
          formData={filters} setFormData={setFilters}
          optionsMap={optionsMap} dense spacing={1.5}
          sx={{ mt: -2 }}
        />
      </BlankCard>
    </Collapse>

    <BlankCard>
      <Table table={table}
        rowCount={data.length}
        renderExpandedRow={handleRowExpansion}
        onRefresh={() => fetchData(setData, setLoading)}
        loading={loading}
      />
    </BlankCard>


    {/* Statement Update Creation Form */}
    <Dialog open={createDialogOpen}
      maxWidth='md' fullWidth
      onClose={handleClose}
      closeAfterTransition={false}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
        Create Statement Update
        <IconButton disabled={submitting}
          onClick={handleClose} sx={{ ml: 'auto' }}
        >
          <FontAwesomeIcon icon={faClose} width='1em' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 3 }}>
        <RenderFormSection
          section={{
            fields: stmtUpdateFields,
            title: 'Create Update Statement',
            icon: <FontAwesomeIcon icon={faFilePen} />,
            headless: true, disableGutters: true
          }}
          formData={formData} setFormData={setFormData}
          optionsMap={optionsMap}
        />
      </DialogContent>

      <DialogActions sx={{ pt: 2.5 }}>
        <Button onClick={handleSubmit}
          sx={{ minWidth: 150, px: 3, py: 1 }}
          startIcon={submitting && <FontAwesomeIcon icon={faSpinnerThird} spin />}
          disabled={submitting} variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  </Stack>
}